import { QridRecord } from '../services/qridService';

/**
 * Format a date string or timestamp for display
 * @param timestamp The date to format
 * @returns Formatted date string
 */
export const formatDate = (timestamp?: any): string => {
  if (!timestamp) return 'N/A';
  
  try {
    // Handle Firestore timestamps
    if (timestamp && typeof timestamp.toDate === 'function') {
      timestamp = timestamp.toDate();
    }
    
    // Handle string dates
    if (typeof timestamp === 'string') {
      timestamp = new Date(timestamp);
    }
    
    // Check if it's a valid date
    if (!(timestamp instanceof Date) || isNaN(timestamp.getTime())) {
      return 'Invalid date';
    }
    
    // Format the date
    return timestamp.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Error';
  }
};

/**
 * Get the color for a status chip based on status value
 * @param status The status string
 * @returns MUI color value
 */
export const getStatusChipColor = (status: string): 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' => {
  if (!status) return 'default';
  
  const statusLower = status.toLowerCase();
  
  if (statusLower.includes('complete') || statusLower === 'active') {
    return 'success';
  } else if (statusLower.includes('pending') || statusLower.includes('waiting')) {
    return 'warning';
  } else if (statusLower.includes('error') || statusLower.includes('fail') || statusLower.includes('cancelled')) {
    return 'error';
  } else if (statusLower.includes('processing') || statusLower.includes('progress')) {
    return 'info';
  } else if (statusLower.includes('new') || statusLower.includes('pickup')) {
    return 'primary';
  } else if (statusLower.includes('return')) {
    return 'secondary';
  }
  
  return 'default';
};

/**
 * Get CSS styling for a status indicator
 * @param status The status string
 * @returns CSS style object
 */
export const getStatusStyle = (status: string) => {
  if (!status) return {};
  
  const statusLower = status.toLowerCase();
  
  if (statusLower.includes('complete') || statusLower === 'active') {
    return { backgroundColor: '#e8f5e9', color: '#2e7d32', borderColor: '#a5d6a7' };
  } else if (statusLower.includes('pending') || statusLower.includes('waiting')) {
    return { backgroundColor: '#fff8e1', color: '#f57c00', borderColor: '#ffe082' };
  } else if (statusLower.includes('error') || statusLower.includes('fail') || statusLower.includes('cancelled')) {
    return { backgroundColor: '#ffebee', color: '#c62828', borderColor: '#ef9a9a' };
  } else if (statusLower.includes('processing') || statusLower.includes('progress')) {
    return { backgroundColor: '#e1f5fe', color: '#0277bd', borderColor: '#81d4fa' };
  } else if (statusLower.includes('new') || statusLower.includes('pickup')) {
    return { backgroundColor: '#e8f5e9', color: '#2e7d32', borderColor: '#a5d6a7' };
  } else if (statusLower.includes('return')) {
    return { backgroundColor: '#f3e5f5', color: '#6a1b9a', borderColor: '#ce93d8' };
  }
  
  return { backgroundColor: '#f5f5f5', color: '#616161', borderColor: '#e0e0e0' };
};

/**
 * Categorize a date string as past, today, or future
 * @param dateStr The date string to categorize
 * @returns 'past', 'today', or 'future'
 */
export const categorizeDateByTime = (dateStr: string | null | undefined): 'past' | 'today' | 'future' => {
  if (!dateStr) return 'past';
  
  try {
    const targetDate = new Date(dateStr);
    const now = new Date();
    
    // Strip time component for comparison
    const targetDay = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    if (targetDay.getTime() < today.getTime()) {
      return 'past';
    } else if (targetDay.getTime() === today.getTime()) {
      return 'today';
    } else {
      return 'future';
    }
  } catch (error) {
    console.error('Error categorizing date:', error);
    return 'past';
  }
};

const parseFlexibleDate = (value?: string): Date | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  // DD-MM-YYYY or DD/MM/YYYY
  if (/^\d{2}[-/]\d{2}[-/]\d{4}$/.test(trimmed)) {
    const [d, m, y] = trimmed.split(/[-/]/).map(Number);
    const date = new Date(y, m - 1, d);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  // YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [y, m, d] = trimmed.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(trimmed);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Exclude records where:
 * - Status is Completed
 * - Return Date is more than 5 days in the past
 */
export const shouldExcludeOldCompletedRecord = (record: QridRecord): boolean => {
  const status = (record.Status || '').trim().toLowerCase();
  if (status !== 'completed') return false;

  const returnDateStr = record.Return_Date || record["Return Date"];
  const returnDate = parseFlexibleDate(returnDateStr);
  if (!returnDate) return false;

  const today = new Date();
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const returnOnly = new Date(returnDate.getFullYear(), returnDate.getMonth(), returnDate.getDate());
  const diffDays = Math.floor((todayOnly.getTime() - returnOnly.getTime()) / (1000 * 60 * 60 * 24));

  return diffDays > 5;
};
